import { DateField } from '@flos/react-ui';
import { eachDayOfInterval, format, getDay, parse } from 'date-fns';
import { useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useAvailabilities } from '../contexts/AvailabilitiesContext';
import { useResourceBundle } from '../contexts/ResourceContext';
import { useAppSelector } from '../hooks/useRedux';
import { navigationActions } from '../store/navigation.slice';
import { DATE_FORMAT, ISO_DATE_FORMAT } from '../utils/date-utils';
import FormGroup from './common/FormGroup';
import TimeSlotNotAvailable from './errors/TimeSlotNotAvailable';
import TimeSlots from './TimeSlots';
import TimeZoneAlert from './TimeZoneAlert';

const isWeekday = (date: Date): boolean => getDay(date) !== 0 && getDay(date) !== 6;

const BookingSchedule = () => {
  const dispatch = useDispatch();
  const selectedDateStr = useAppSelector((state) => state.navigation.bookingDate);
  const selectedDate = selectedDateStr ? parse(selectedDateStr, DATE_FORMAT, new Date()) : undefined;
  const knownSubmissionError = useAppSelector((state) => state.navigation.submission.knownError);

  const resource = useResourceBundle();
  const headerRef = useRef<HTMLHeadingElement>(null);

  const { startDate, endDate, availabilities } = useAvailabilities();

  // show time slot not available message if there's a known submission error, and
  // selected date is empty
  const showTimeSlotUnavailable = !!knownSubmissionError && !selectedDate;

  const availableDates = new Set<string>(
    Object.entries(availabilities)
      .filter(([, info]) => info.isAvailable)
      .map(([date]) => parse(date, ISO_DATE_FORMAT, new Date()))
      .map((date) => format(date, DATE_FORMAT))
  );

  const actualStartDate = forwardStartDateIfDisabled(availableDates, startDate, endDate);

  const disabledDates = eachDayOfInterval({ start: startDate, end: endDate }).filter(
    (date) => !availableDates.has(format(date, DATE_FORMAT))
  );

  const setDate = (date: Date) => {
    dispatch(navigationActions.selectBookingDate(format(date, DATE_FORMAT)));
  };

  const importantStyle = {
    fontSize: '19px',
    importantProperty: 'value !important',
  };

  return (
    <div style={importantStyle}>
      {showTimeSlotUnavailable && <TimeSlotNotAvailable />}
      <TimeZoneAlert />
      <h4 ref={headerRef}>{resource['label.schedule.select-date']}</h4>
      <FormGroup className="u-spacing-stack-m">
        <DateField
          label={resource['label.schedule.select-date.input']}
          required
          dateFormat="dd-MM-yyyy"
          filterDate={isWeekday}
          minDate={actualStartDate}
          maxDate={endDate}
          selected={selectedDate}
          onChange={setDate}
          excludeDates={disabledDates}
        />
      </FormGroup>

      {selectedDate && (
        <FormGroup>
          {/* key attribute forces component to re-mount, allowing 'activeIndex' in Accordion to take effect. */}
          <TimeSlots
            key={selectedDateStr}
            slots={selectedDate ? availabilities[format(selectedDate, ISO_DATE_FORMAT)]?.slots : undefined}
            date={selectedDate}
          />
        </FormGroup>
      )}
    </div>
  );
};

export default BookingSchedule;

/**
 * This is a workaround for date picker:
 *
 * <b>Issue</b>
 *
 * By default when date picker is opened, today's date is preselected.
 * Even if today's date is disabled, it'll still be preselected.
 *
 * <b>Workaround</b>
 *
 * It happens that the date picker respects `minDate`, so if `minDate` is set to the day after,
 * the date after today is preselected upon opening the date picker in a prestint state.
 *
 * <b>Logic</b>
 *
 * If today's date isn't found in `availableDates`, forward `minDate` by a day
 * and keep going until an available date if found, else fallback to today's date.
 *
 * @param availableDates Dates in dd-MM-yyyy format (string)
 */
export const forwardStartDateIfDisabled = (availableDates: Set<string>, startDate: Date, endDate: Date): Date => {
  const nextAvailableDate = eachDayOfInterval({ start: startDate, end: endDate }).find((date) =>
    availableDates.has(format(date, DATE_FORMAT))
  );
  return nextAvailableDate ?? startDate;
};
